/**
 * ComponentGate — render-time switch driven by tenant `components` flags.
 *
 * Reads `useTenantConfig()` and gates `children` by looking up
 * `config.components[id]`:
 *
 *   - status not `live`/`paused`  → null. Boot fallbacks (loading,
 *                                   archived, not-found, error) are
 *                                   handled higher up the tree; below
 *                                   that line the gate has no config to
 *                                   answer with, so it stays out of the
 *                                   way.
 *   - flag === false              → render `fallback` (default `null`).
 *   - flag === true               → render `children`.
 *   - flag === undefined          → render `children` AND emit a
 *                                   one-time `component-gate-unknown`
 *                                   warning in dev. Treat unknown ids as
 *                                   "shown by default" so a missing
 *                                   catalogue entry never blanks the
 *                                   surface, but surface the typo to the
 *                                   developer.
 *
 * Returns the children directly (no DOM wrapper) so a hidden component
 * collapses cleanly out of any flex/grid layout above it.
 */

import { useEffect, type ReactElement, type ReactNode } from 'react';
import { reportEvent } from '../../observability.js';
import { useTenantConfig } from '../../tenant/useTenantConfig.js';

interface ComponentGateProps {
  /** Component flag id (matches `RuntimeConfig.components` key). */
  id: string;
  children: ReactNode;
  /** Optional fallback when the component is disabled. Defaults to null. */
  fallback?: ReactNode;
}

/**
 * Process-lifetime set of ids we have already warned about. Keeps the
 * dev console useful — every typo logs exactly once, no matter how many
 * times the gate re-renders.
 */
const warnedUnknownIds = new Set<string>();

export function ComponentGate({
  id,
  children,
  fallback = null,
}: ComponentGateProps): ReactElement | null {
  const status = useTenantConfig();

  // Effect must be declared unconditionally to satisfy the rules of
  // hooks. We compute the "should warn" decision inside so the early
  // returns below don't change hook order.
  const flag =
    status.kind === 'live' || status.kind === 'paused'
      ? status.config.components[id]
      : null;

  useEffect(() => {
    if (flag === undefined && !warnedUnknownIds.has(id)) {
      warnedUnknownIds.add(id);
      reportEvent('component-gate-unknown', { id });
    }
  }, [flag, id]);

  if (status.kind !== 'live' && status.kind !== 'paused') {
    return null;
  }

  if (flag === false) {
    return <>{fallback}</>;
  }

  // `true` or `undefined` → render children. Undefined means the engine
  // never registered this component id; treat as "shown by default".
  return <>{children}</>;
}
